import React, { useState, useEffect } from 'react';
import { authorizedRequest } from '../../api/authorizedRequest';
import Swal from 'sweetalert2';

interface ResetPasswordModalProps {
  show: boolean;
  onClose: () => void;
  userId: string | null;
  employeeName: string | null;
}

const ResetPasswordModal: React.FC<ResetPasswordModalProps> = ({ show, onClose, userId, employeeName }) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ password?: string; confirmPassword?: string }>({});

  useEffect(() => {
    if (show) {
      // Reset form when modal opens
      setPassword('');
      setConfirmPassword('');
      setErrors({});
    }
  }, [show]);

  const validateForm = (): boolean => {
    const newErrors: { password?: string; confirmPassword?: string } = {};

    if (!password) {
      newErrors.password = 'Password is required';
    } else if (password.length < 3) {
      newErrors.password = 'Password must be at least 3 characters';
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = 'Confirm password is required';
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!userId) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'User ID is missing',
      });
      return;
    }

    setLoading(true);
    try {
      await authorizedRequest({
        url: '/Auth/admin-reset-password',
        method: 'POST',
        data: {
          userId: userId,
          newPassword: password,
        },
      });

      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: `Password for ${employeeName} has been reset successfully!`,
      });

      onClose();
    } catch (error: any) {
      const errorMessage = error.response?.data?.errors
        ? error.response.data.errors.map((e: any) => e.description || e).join(', ')
        : error.message || 'Failed to reset password';

      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  if (!show) {
    return null;
  }

  return (
    <>
      <div className="modal-backdrop fade show" onClick={onClose} style={{ zIndex: 1040 }}></div>
      <div 
        className="modal fade show" 
        style={{ display: 'block', zIndex: 1050 }} 
        tabIndex={-1}
        role="dialog"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Reset Password for {employeeName}</h5>
              <button type="button" className="close" onClick={onClose} disabled={loading}>
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label htmlFor="password">New Password <span className="text-danger">*</span></label>
                  <input
                    type="password"
                    className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loading}
                    placeholder="Enter new password"
                  />
                  {errors.password && <div className="invalid-feedback">{errors.password}</div>}
                </div>
                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirm Password <span className="text-danger">*</span></label>
                  <input
                    type="password"
                    className={`form-control ${errors.confirmPassword ? 'is-invalid' : ''}`}
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={loading}
                    placeholder="Confirm new password"
                  />
                  {errors.confirmPassword && <div className="invalid-feedback">{errors.confirmPassword}</div>}
                </div>
                <div className="alert alert-info mt-3" role="alert">
                  <small>
                    <strong>Note:</strong> As an admin, you can reset the password without needing the old password. 
                    Make sure to inform the user about their new password.
                  </small>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Resetting...' : 'Reset Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default ResetPasswordModal;
